import { useUserActionsContext } from 'contexts/userActions/useUserActionsContext'
import { addTourBanner, removeTourBanner } from 'ducks/auth'
import { FujiTourType, tourSelectors } from 'ducks/tour'
import { useFeatureFlagsLoaded } from 'hooks/featureFlags/useFeatureFlagsLoaded'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from 'types/state'
import { useToursAvailable } from './useToursAvailable'

type AutoShowTourType = Exclude<FujiTourType, 'main'>

const TOUR_BANNER_CRITERIA: { [tourType in AutoShowTourType]: TourBannerCriteria } = {
  manage: {
    actions: ['fuji_tour-manage_tour_finish', 'fuji_tour-manage_tour_hidden'],
    pages: ['/manage'],
  },
  project_list: {
    actions: ['fuji_tour-list_tour_finish', 'fuji_tour-list_tour_hidden'],
    pages: ['/projects'],
  },
  payments: { actions: [], pages: [] },
  ada_chat: { actions: [], pages: [] },
}

type TourBannerCriteria = {
  actions: string[]
  pages: string[]
}

/**
 * This hook auto-shows tour banners based on whether the finish/hidden actions have been taken.
 */
export const useTourAutoShow = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { loaded: actionsLoaded, checkAction } = useUserActionsContext()
  const shouldShowTours = useSelector((state: RootState) => state?.auth?.user?.show_tour_banner) || {}
  const toursAvailable = useToursAvailable()
  const featureFlagsLoaded = useFeatureFlagsLoaded()

  const currentTour = useSelector(tourSelectors.tour)
  const currentTourFixed: FujiTourType = currentTour === 'fuji' ? 'main' : (currentTour as FujiTourType)

  useEffect(() => {
    if (!toursAvailable || !actionsLoaded || !featureFlagsLoaded) return

    for (const i in TOUR_BANNER_CRITERIA) {
      const tour = i as AutoShowTourType
      const shouldShowTour = shouldShowTours[tour]
      const bannerCriteria = TOUR_BANNER_CRITERIA[tour]
      const hideTourActions = bannerCriteria.actions.filter((x) => checkAction(x))
      const onPage =
        bannerCriteria.pages.includes(location.pathname) ||
        bannerCriteria.pages.some((path) => location.pathname.includes(path))

      const shouldShow = onPage && !hideTourActions.length && currentTourFixed !== tour

      if (shouldShowTour === undefined || (shouldShow && !shouldShowTour.shouldShow && !shouldShowTour.userHidIt)) {
        dispatch(!shouldShow ? removeTourBanner(true, tour) : addTourBanner(tour))
      }
    }
  }, [
    toursAvailable,
    shouldShowTours,
    actionsLoaded,
    location.pathname,
    checkAction,
    featureFlagsLoaded,
    currentTourFixed,
    dispatch,
  ])

  useEffect(() => {
    if (currentTourFixed && shouldShowTours[currentTourFixed]?.shouldShow) {
      dispatch(removeTourBanner(false, currentTourFixed))
    }
  }, [currentTourFixed, shouldShowTours, dispatch])
}
